import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useToast } from '../../components/Toast';
import { useAuth } from '../../auth/AuthProvider';
import Header from '../../components/Header';
import StatusBadge from '../../components/StatusBadge';
import KpiCard from '../../components/KpiCard';
import SearchableSelect from '../../components/SearchableSelect';
import EnterNavForm from '../../components/EnterNavForm';
import useUrlState from '../../hooks/useUrlState';
import { extractApiError } from '../../utils/apiError';
import { colors, radius, shadows, spacing } from '../../styles/theme';
import { invalidateQueries } from '../../hooks/useCachedQuery';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const getAuthHeaders = (extra = {}) => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}`, ...extra } : extra;
};

// ── Ortak stiller (tema token'ları) ─────────────────────────────────────────
const cardStyle = {
  background: colors.surface,
  borderRadius: radius.lg,
  boxShadow: shadows.sm,
  border: `1px solid ${colors.border}`,
};
const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  border: `1px solid ${colors.border}`,
  borderRadius: radius.sm,
  fontSize: '14px',
  boxSizing: 'border-box',
  background: colors.surface,
  color: colors.textPrimary,
};
const labelStyle = {
  display: 'block',
  marginBottom: spacing.xs,
  fontSize: '12px',
  fontWeight: 600,
  color: colors.textSecondary,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
};
const btn = (bg, fg = '#fff') => ({
  padding: '10px 18px',
  background: bg,
  color: fg,
  border: 'none',
  borderRadius: radius.sm,
  cursor: 'pointer',
  fontWeight: 600,
  fontSize: '14px',
});
const ghostBtn = {
  padding: '10px 18px',
  background: colors.surface,
  color: colors.textSecondary,
  border: `1px solid ${colors.border}`,
  borderRadius: radius.sm,
  cursor: 'pointer',
  fontWeight: 600,
  fontSize: '14px',
};

const miktarFmt = (n) => {
  const num = Number(n);
  if (!Number.isFinite(num)) return '0';
  return String(+num.toFixed(2));
};

// ── Onay modalı (window.confirm yerine markalı + erişilebilir) ───────────────
const ConfirmModal = ({ open, title, message, confirmLabel, danger, busy, onConfirm, onCancel }) => {
  const confirmRef = useRef(null);
  const restoreRef = useRef(null);

  // Açılışta tetikleyen öğeyi sakla + odağı onay butonuna taşı; kapanışta geri ver.
  useEffect(() => {
    if (!open) return undefined;
    restoreRef.current = document.activeElement;
    confirmRef.current?.focus();
    return () => {
      if (restoreRef.current && restoreRef.current.focus) restoreRef.current.focus();
    };
  }, [open]);

  // Esc ile kapat (meşgul değilken — overlay tık davranışıyla tutarlı).
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onCancel?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, busy, onCancel]);

  if (!open) return null;
  return (
    <div
      onClick={onCancel}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 10000, padding: spacing.base,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-modal-title"
        aria-describedby="confirm-modal-desc"
        style={{
          ...cardStyle, maxWidth: '440px', width: '100%',
          padding: spacing.xl, boxShadow: shadows.xl,
        }}
      >
        <h3 id="confirm-modal-title" style={{ margin: 0, marginBottom: spacing.sm, fontSize: '17px', color: colors.textPrimary }}>{title}</h3>
        <p id="confirm-modal-desc" style={{ margin: 0, marginBottom: spacing.xl, fontSize: '14px', lineHeight: 1.6, color: colors.textSecondary }}>{message}</p>
        <div style={{ display: 'flex', gap: spacing.sm, justifyContent: 'flex-end' }}>
          <button onClick={onCancel} disabled={busy} style={ghostBtn}>Vazgeç</button>
          <button
            ref={confirmRef}
            onClick={onConfirm}
            disabled={busy}
            style={btn(busy ? colors.textTertiary : (danger ? colors.danger : colors.success))}
          >
            {busy ? 'İşleniyor…' : (confirmLabel || 'Onayla')}
          </button>
        </div>
      </div>
    </div>
  );
};

const UretimKaydiPage = () => {
  useAuth();
  const toast = useToast();

  // ── URL state (geri dönüş / yenileme / paylaşımda korunur) ──
  const [mod, setMod] = useUrlState('mod', '');                 // '' = liste, 'yeni' = form
  const [runParam, setRunParam] = useUrlState('run', '');       // '' = yok, aksi: detay run id
  const [arama, setArama] = useUrlState('arama', '', { debounceMs: 300 });
  const [durumFiltre, setDurumFiltre] = useUrlState('durum', '');
  const [lokasyonFiltre, setLokasyonFiltre] = useUrlState('lokasyon', '');
  const [tarihBaslangicFiltre, setTarihBaslangicFiltre] = useUrlState('tarih_baslangic', '');
  const [tarihBitisFiltre, setTarihBitisFiltre] = useUrlState('tarih_bitis', '');

  // Birden çok URL anahtarını TEK yazımla güncelle. (react-router'da art arda
  // setSearchParams çağrıları aynı render closure'ındaki bayat snapshot'ı okur →
  // birbirini ezer; useUrlState setter'ları aynı tick'te kullanılınca yalnız
  // sonuncusu kalır. Atomik tek yazım bu clobber'ı önler; useUrlState'ler URL'i
  // dışarıdan değişince local'larını zaten senkronlar.)
  const [, setSearchParams] = useSearchParams();
  const patchUrl = useCallback((removeKeys = [], setEntries = {}) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      removeKeys.forEach((k) => next.delete(k));
      Object.entries(setEntries).forEach(([k, v]) => {
        if (v === '' || v == null) next.delete(k); else next.set(k, String(v));
      });
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // ── Özel üretim kuralları ──
  // Sadece burada tanımlanan reçeteler mevcut genel davranıştan ayrılır.
  // İleride yeni özel reçeteler çıkarsa bu listeye koduyla eklenebilir.
  const OZEL_URETIM_KURALLARI = {
    'AUK-013': { birim: 'PORSIYON', tamSayi: true },
  };

  // ── Form state ──
  const [recipeId, setRecipeId] = useState('');
  const [lokasyonId, setLokasyonId] = useState('');
  const [uretimMiktari, setUretimMiktari] = useState('');
  const [birim, setBirim] = useState('KG');
  const [uretimTarihi, setUretimTarihi] = useState(new Date().toISOString().split('T')[0]);
  const [notlar, setNotlar] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // ── Data ──
  const [receteler, setReceteler] = useState([]);
  const [lokasyonlar, setLokasyonlar] = useState([]);
  const [runs, setRuns] = useState([]);
  const [selectedRun, setSelectedRun] = useState(null);
  const [detayLoading, setDetayLoading] = useState(false);
  const [loading, setLoading] = useState(true);

  // ── Onay modalı + aksiyon meşguliyeti ──
  const [confirm, setConfirm] = useState(null);
  const [actionBusy, setActionBusy] = useState(false);

  const refreshRuns = useCallback(async () => {
    try {
      const rs = await fetch(`${API_URL}/api/production/recipe-runs`, { headers: getAuthHeaders() }).then(r => r.json());
      setRuns(Array.isArray(rs) ? rs : []);
    } catch { /* sessiz — toast zaten ilgili aksiyonda */ }
  }, []);

  // İlk yükleme
  useEffect(() => {
    Promise.all([
      fetch(`${API_URL}/api/recipes?durum=AKTIF`, { headers: getAuthHeaders() }).then(r => r.json()),
      fetch(`${API_URL}/api/lokasyonlar`, { headers: getAuthHeaders() }).then(r => r.json()),
      fetch(`${API_URL}/api/production/recipe-runs`, { headers: getAuthHeaders() }).then(r => r.json()),
    ]).then(([recs, loks, rs]) => {
      setReceteler(Array.isArray(recs) ? recs : []);
      setLokasyonlar(Array.isArray(loks) ? loks : []);
      setRuns(Array.isArray(rs) ? rs : []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  // Detay: run URL param'ı değişince ilgili kaydı çek (yenileme/derin-link destekli)
  useEffect(() => {
    if (!runParam) { setSelectedRun(null); return; }
    let cancelled = false;
    setDetayLoading(true);
    fetch(`${API_URL}/api/production/recipe-runs/${runParam}`, { headers: getAuthHeaders() })
      .then(r => r.json())
      .then(d => { if (!cancelled) setSelectedRun(d && d.id ? d : null); })
      .catch(() => { if (!cancelled) setSelectedRun(null); })
      .finally(() => { if (!cancelled) setDetayLoading(false); });
    return () => { cancelled = true; };
  }, [runParam]);

  const reloadSelected = useCallback(async (id) => {
    try {
      const d = await fetch(`${API_URL}/api/production/recipe-runs/${id}`, { headers: getAuthHeaders() }).then(r => r.json());
      setSelectedRun(d && d.id ? d : null);
    } catch { /* sessiz */ }
  }, []);

  const parseErr = async (res, fallback) => {
    try {
      const j = await res.json();
      return extractApiError(j && 'detail' in j ? j.detail : j, fallback);
    } catch {
      return fallback;
    }
  };

  // ── KPI'lar (tüm kayıtlar üzerinden) ──
  const today = new Date().toISOString().split('T')[0];
  const taslakSayisi = useMemo(() => runs.filter(r => r.durum === 'TASLAK').length, [runs]);
  const tamamlananSayisi = useMemo(() => runs.filter(r => r.durum === 'TAMAMLANDI').length, [runs]);
  const uretilenKg = useMemo(
    () => runs.filter(r => r.durum === 'TAMAMLANDI').reduce((s, r) => s + (r.uretim_miktari || 0), 0),
    [runs]
  );
  const bugunSayisi = useMemo(() => runs.filter(r => r.uretim_tarihi === today).length, [runs, today]);

  // ── Filtrelenmiş liste ──
  const filtreliRuns = useMemo(() => {
    const q = (arama || '').toLocaleLowerCase('tr');
    return runs.filter(r => {
      if (durumFiltre && r.durum !== durumFiltre) return false;
      if (lokasyonFiltre && String(r.lokasyon_id) !== String(lokasyonFiltre)) return false;
      if (tarihBaslangicFiltre && r.uretim_tarihi < tarihBaslangicFiltre) return false;
      if (tarihBitisFiltre && r.uretim_tarihi > tarihBitisFiltre) return false;
      if (q) {
        const hay = `${r.uretim_no || ''} ${r.recipe_ad || ''} ${r.lokasyon_ad || ''}`.toLocaleLowerCase('tr');
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [runs, durumFiltre, lokasyonFiltre, tarihBaslangicFiltre, tarihBitisFiltre, arama]);

  const filtreAktif = Boolean(arama || durumFiltre || lokasyonFiltre || tarihBaslangicFiltre || tarihBitisFiltre);
  // setArama('') → arama'nın bekleyen debounce timer'ını iptal eder + input'u anında temizler;
  // patchUrl → beş anahtarı TEK yazımla siler (clobber'sız).
  const clearFiltreler = () => { setArama(''); patchUrl(['arama', 'durum', 'lokasyon', 'tarih_baslangic', 'tarih_bitis']); };

  // ── Aksiyonlar ──
  const handleCreate = async () => {
    if (!recipeId) { toast.error('Reçete seçiniz'); return; }
    if (!lokasyonId) { toast.error('Lokasyon seçiniz'); return; }
    if (!uretimMiktari || parseFloat(uretimMiktari) <= 0) { toast.error('Geçerli bir üretim miktarı giriniz'); return; }

    const createRecipe = receteler.find(r => String(r.id) === String(recipeId));
    const createKural = createRecipe ? OZEL_URETIM_KURALLARI[createRecipe.recete_kodu] : null;
    if (createKural?.tamSayi && !Number.isInteger(Number(uretimMiktari))) {
      toast.error(`${createRecipe.recete_kodu} için üretim miktarı tam sayı porsiyon olmalıdır`);
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/production/recipe-runs`, {
        method: 'POST',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          recipe_id: parseInt(recipeId),
          lokasyon_id: parseInt(lokasyonId),
          uretim_miktari: parseFloat(uretimMiktari),
          birim: (birim || '').trim().toUpperCase() || 'KG',
          uretim_tarihi: uretimTarihi,
          notlar: notlar || null,
        }),
      });
      if (!res.ok) { toast.error(await parseErr(res, 'Üretim kaydı oluşturulamadı')); setSubmitting(false); return; }
      const data = await res.json();
      toast.success(`Üretim kaydı oluşturuldu: ${data.uretim_no} (${data.hammadde_sayisi} hammadde)`);
      // Formu temizle
      setRecipeId(''); setUretimMiktari(''); setNotlar('');
      // Listeyi yenile + yeni kaydın detayına geç. mod-temizleme ve run-set TEK
      // atomik yazımda (aksi halde bayat 'mod=yeni' kalır → detaydan geri form'a düşer).
      await refreshRuns();
      patchUrl(['mod'], { run: String(data.id) });
    } catch (err) {
      toast.error('Sunucu hatası: ' + err.message);
    }
    setSubmitting(false);
  };

  // force=true → backend yetersiz-stok engelini bilinçli aşar (negatif stok oluşur,
  // negatif-stok panelinde izlenir; yetki backend _require_roles ile zorunlu).
  const doTamamla = async (runId, force = false) => {
    setActionBusy(true);
    try {
      const url = `${API_URL}/api/production/recipe-runs/${runId}/tamamla${force ? '?force=true' : ''}`;
      const res = await fetch(url, {
        method: 'POST',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
      });
      if (!res.ok) {
        // Gövdeyi TEK kez oku: hem yetersiz-stok tespiti hem okunur mesaj için.
        let detail = null;
        try { const j = await res.json(); detail = j && typeof j === 'object' && 'detail' in j ? j.detail : j; } catch { /* gövde JSON değil */ }
        const yetersiz = detail && typeof detail === 'object' && Array.isArray(detail.yetersiz) ? detail.yetersiz : null;
        setActionBusy(false);
        await reloadSelected(runId); // güncel yeterli/yetersiz durumu görünsün
        if (yetersiz && !force) {
          askForce(runId, yetersiz); // yarış: stok aradan değişmiş → override teklif et
        } else {
          setConfirm(null);
          toast.error(extractApiError(detail, 'Üretim tamamlanamadı'));
        }
        return;
      }
      const data = await res.json();
      if (data.uyarilar && data.uyarilar.length > 0) {
        toast.warning(`Tamamlandı (${data.hareket_sayisi} hareket) — uyarılar: ${data.uyarilar.join(', ')}`);
      } else {
        toast.success(`Üretim tamamlandı: ${data.hareket_sayisi} stok hareketi oluşturuldu`);
      }
      invalidateQueries(['stok', 'dashboard', 'uretim']);
      await Promise.all([reloadSelected(runId), refreshRuns()]);
    } catch (err) {
      toast.error('Sunucu hatası: ' + err.message);
    }
    setActionBusy(false);
    setConfirm(null);
  };

  const doDelete = async (runId) => {
    setActionBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/production/recipe-runs/${runId}`, { method: 'DELETE', headers: getAuthHeaders() });
      if (!res.ok) { toast.error(await parseErr(res, 'Üretim kaydı silinemedi')); setActionBusy(false); setConfirm(null); return; }
      toast.success('Üretim kaydı silindi');
      setRunParam('');
      await refreshRuns();
    } catch (err) {
      toast.error('Sunucu hatası: ' + err.message);
    }
    setActionBusy(false);
    setConfirm(null);
  };

  // Yetersiz stok override onayı: eksik malzemeleri okunur listeler + negatif stok uyarısı.
  const askForce = (runId, yetersiz) => {
    const list = (yetersiz || [])
      .map((y) => `${y.malzeme ?? y.ad ?? 'Malzeme'} (mevcut ${miktarFmt(y.mevcut)} / gerekli ${miktarFmt(y.gerekli)})`)
      .join(', ');
    setConfirm({
      title: 'Yetersiz stok — yine de tamamla?',
      message: `Şu hammaddeler yetersiz: ${list}. Yine de tamamlarsanız eksik kalemler negatif stoğa düşer (negatif-stok panelinde izlenir). Devam edilsin mi?`,
      confirmLabel: 'Yine de Tamamla',
      danger: true,
      onConfirm: () => doTamamla(runId, true),
    });
  };

  const askTamamla = (runId) => {
    // Stok zaten yetersizse boş yere force'suz denemeden doğrudan override onayı göster.
    if (yetersizLines.length > 0) {
      askForce(runId, yetersizLines.map((l) => ({
        malzeme: l.malzeme_ad || `Malzeme #${l.malzeme_id}`,
        mevcut: l.mevcut_stok,
        gerekli: l.teorik_miktar,
      })));
      return;
    }
    setConfirm({
      title: 'Üretimi Tamamla',
      message: 'Bu üretim tamamlanacak; hammadde çıkışı ve üretilen ürün girişi stok hareketleri oluşturulacak. Devam edilsin mi?',
      confirmLabel: 'Tamamla',
      danger: false,
      onConfirm: () => doTamamla(runId, false),
    });
  };
  const askDelete = (runId) => setConfirm({
    title: 'Taslağı Sil',
    message: 'Bu taslak üretim kaydı kalıcı olarak silinecek. Bu işlem geri alınamaz. Devam edilsin mi?',
    confirmLabel: 'Sil',
    danger: true,
    onConfirm: () => doDelete(runId),
  });

  const handleRecipeChange = (id) => {
    setRecipeId(id);
    const rec = receteler.find(r => String(r.id) === String(id));
    if (!rec) return;

    const ozelKural = OZEL_URETIM_KURALLARI[rec.recete_kodu];

    if (ozelKural) {
      setBirim(ozelKural.birim);
      setUretimMiktari('');
      return;
    }

    if (rec.porsiyon_birim) setBirim(rec.porsiyon_birim);
  };

  const receteOptions = receteler.map(r => ({ value: r.id, label: `${r.ad}${r.tip ? ` (${r.tip})` : ''}` }));
  const lokasyonOptions = lokasyonlar.map(l => ({ value: l.id, label: `${l.ad} (${l.tip})` }));
  const selectedRecipe = receteler.find(r => r.id === (recipeId ? parseInt(recipeId) : null));
  const selectedOzelKural = selectedRecipe ? OZEL_URETIM_KURALLARI[selectedRecipe.recete_kodu] : null;

  // ── Detay türetilmiş durum ──
  const cikisLines = (selectedRun?.lines || []).filter(l => l.hareket_tipi === 'CIKIS');
  const yetersizLines = cikisLines.filter(l => !l.yeterli);
  const uretimeHazir = !selectedRun?.kilitli && cikisLines.length > 0 && yetersizLines.length === 0;

  if (loading && receteler.length === 0) {
    return <div style={{ padding: spacing['3xl'], textAlign: 'center', color: colors.textSecondary }}>Yükleniyor…</div>;
  }

  const detayGoster = Boolean(runParam);
  const formGoster = !detayGoster && mod === 'yeni';
  const listeGoster = !detayGoster && !formGoster;

  return (
    <div>
      <Header title="Üretim Kaydı" />
      <div style={{ padding: spacing.lg, background: colors.background, minHeight: 'calc(100vh - 60px)' }}>

        {/* ════ LİSTE ════ */}
        {listeGoster && (
          <>
            {/* KPI Kartları */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: spacing.base, marginBottom: spacing.lg }}>
              <KpiCard title="Taslak (Bekleyen)" value={taslakSayisi} icon="📝" color={colors.warning}
                subtitle="Tamamlanmayı bekliyor" />
              <KpiCard title="Tamamlanan" value={tamamlananSayisi} icon="✅" color={colors.success}
                subtitle="Stoğa işlenmiş üretim" />
              <KpiCard title="Üretilen Miktar" value={`${miktarFmt(uretilenKg)} kg`} icon="⚖️" color={colors.info}
                subtitle="Tamamlanan kayıtların toplamı" />
              <KpiCard title="Bugün" value={bugunSayisi} icon="📅" color={colors.primary}
                subtitle="Bugün tarihli kayıt" />
            </div>

            {/* Araç çubuğu: durum sekmeleri + arama + filtreler + Yeni */}
            <div style={{ ...cardStyle, padding: spacing.base, marginBottom: spacing.lg }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: spacing.md, alignItems: 'center' }}>
                {/* Durum sekmeleri */}
                <div style={{ display: 'inline-flex', background: colors.backgroundAlt, padding: '3px', borderRadius: radius.sm, border: `1px solid ${colors.border}` }}>
                  {[{ k: '', l: 'Tümü' }, { k: 'TASLAK', l: 'Taslak' }, { k: 'TAMAMLANDI', l: 'Tamamlanan' }].map(t => (
                    <button key={t.k || 'all'} onClick={() => setDurumFiltre(t.k)} aria-pressed={durumFiltre === t.k} style={{
                      padding: '7px 14px', border: 'none', borderRadius: radius.sm, cursor: 'pointer',
                      fontSize: '13px', fontWeight: 600,
                      background: durumFiltre === t.k ? colors.surface : 'transparent',
                      color: durumFiltre === t.k ? colors.primary : colors.textSecondary,
                      boxShadow: durumFiltre === t.k ? shadows.sm : 'none',
                    }}>{t.l}</button>
                  ))}
                </div>

                <input
                  value={arama}
                  onChange={(e) => setArama(e.target.value)}
                  placeholder="Reçete / üretim no / lokasyon ara…"
                  style={{ ...inputStyle, flex: '1 1 240px', minWidth: '200px', width: 'auto' }}
                />

                <select value={lokasyonFiltre} onChange={(e) => setLokasyonFiltre(e.target.value)}
                  style={{ ...inputStyle, width: 'auto', flex: '0 0 auto', cursor: 'pointer' }}>
                  <option value="">Tüm lokasyonlar</option>
                  {lokasyonlar.map(l => <option key={l.id} value={l.id}>{l.ad}</option>)}
                </select>

                <div style={{ display: 'inline-flex', alignItems: 'center', gap: spacing.xs }}>
                  <input type="date" value={tarihBaslangicFiltre} onChange={(e) => setTarihBaslangicFiltre(e.target.value)}
                    title="Başlangıç tarihi"
                    style={{ ...inputStyle, width: 'auto', flex: '0 0 auto', cursor: 'pointer' }} />
                  <span style={{ color: colors.textSecondary, fontSize: '13px' }}>–</span>
                  <input type="date" value={tarihBitisFiltre} onChange={(e) => setTarihBitisFiltre(e.target.value)}
                    title="Bitiş tarihi"
                    style={{ ...inputStyle, width: 'auto', flex: '0 0 auto', cursor: 'pointer' }} />
                </div>

                {filtreAktif && (
                  <button onClick={clearFiltreler} style={{ ...ghostBtn, padding: '8px 12px' }}>Temizle</button>
                )}

                <button onClick={() => { setMod('yeni'); }} style={{ ...btn(colors.primary), marginLeft: 'auto' }}>
                  + Yeni Üretim
                </button>
              </div>
            </div>

            {/* Tablo */}
            <div style={{ ...cardStyle, overflow: 'hidden' }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: colors.secondary, color: '#fff', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                      <th style={{ padding: '12px 14px', textAlign: 'left' }}>Üretim No</th>
                      <th style={{ padding: '12px 14px', textAlign: 'left' }}>Reçete</th>
                      <th style={{ padding: '12px 14px', textAlign: 'left' }}>Lokasyon</th>
                      <th style={{ padding: '12px 14px', textAlign: 'right' }}>Miktar</th>
                      <th style={{ padding: '12px 14px', textAlign: 'left' }}>Tarih</th>
                      <th style={{ padding: '12px 14px', textAlign: 'center' }}>Durum</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtreliRuns.length === 0 ? (
                      <tr><td colSpan="6" style={{ padding: spacing['3xl'], textAlign: 'center', color: colors.textTertiary }}>
                        {runs.length === 0
                          ? 'Henüz üretim kaydı yok. “+ Yeni Üretim” ile başlayın.'
                          : 'Filtreyle eşleşen kayıt bulunamadı.'}
                      </td></tr>
                    ) : filtreliRuns.map(run => (
                      <tr key={run.id}
                        onClick={() => setRunParam(String(run.id))}
                        tabIndex={0}
                        role="button"
                        aria-label={`${run.uretim_no} detayını aç`}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setRunParam(String(run.id)); } }}
                        style={{ borderBottom: `1px solid ${colors.borderLight}`, cursor: 'pointer', outlineOffset: '-2px' }}
                        onMouseEnter={e => e.currentTarget.style.background = colors.backgroundAlt}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                        onFocus={e => e.currentTarget.style.background = colors.backgroundAlt}
                        onBlur={e => e.currentTarget.style.background = 'transparent'}>
                        <td style={{ padding: '12px 14px', fontWeight: 600, color: colors.textPrimary }}>{run.uretim_no}</td>
                        <td style={{ padding: '12px 14px', color: colors.textPrimary }}>{run.recipe_ad || '—'}</td>
                        <td style={{ padding: '12px 14px', color: colors.textSecondary }}>{run.lokasyon_ad || '—'}</td>
                        <td style={{ padding: '12px 14px', textAlign: 'right', fontWeight: 600, color: colors.textPrimary }}>
                          {miktarFmt(run.uretim_miktari)} {run.birim}
                        </td>
                        <td style={{ padding: '12px 14px', color: colors.textSecondary }}>{run.uretim_tarihi}</td>
                        <td style={{ padding: '12px 14px', textAlign: 'center' }}><StatusBadge status={run.durum} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {filtreliRuns.length > 0 && (
                <div style={{ padding: '10px 14px', fontSize: '12px', color: colors.textTertiary, borderTop: `1px solid ${colors.borderLight}` }}>
                  {filtreliRuns.length} kayıt{filtreAktif ? ` (toplam ${runs.length})` : ''}
                </div>
              )}
            </div>
          </>
        )}

        {/* ════ FORM ════ */}
        {formGoster && (
          <div style={{ ...cardStyle, padding: spacing.xl, maxWidth: '760px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: spacing.lg }}>
              <h3 style={{ margin: 0, fontSize: '17px', color: colors.textPrimary }}>Yeni Üretim Kaydı</h3>
              <button onClick={() => setMod('')} style={ghostBtn}>← Listeye Dön</button>
            </div>

            {/* Enter ile alan-gezme: son alandan (Notlar=textarea newline) sonra "Oluştur" butonuna
                ulaşılır; onSubmit=handleCreate GÜVENLİ (yalnız TASLAK oluşturur, stok tüketmez).
                Riskli "Üretimi Tamamla" detay görünümünde — bu forma bağlı DEĞİL. Tek satırlı form →
                onRowAdd YOK. SearchableSelect (Reçete/Lokasyon) açıkken Enter'ı kendi yutar (advance yapmaz). */}
            <EnterNavForm onSubmit={handleCreate}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: spacing.base, marginBottom: spacing.base }}>
              <div>
                <label style={labelStyle}>Reçete *</label>
                <SearchableSelect options={receteOptions} value={recipeId ? parseInt(recipeId) : ''} onChange={handleRecipeChange} placeholder="Reçete seçiniz…" />
                {selectedRecipe && (
                  <div style={{ fontSize: '12px', color: colors.textTertiary, marginTop: spacing.xs }}>
                    Porsiyon: {miktarFmt(selectedRecipe.porsiyon_miktar)} {selectedRecipe.porsiyon_birim || ''}
                    {selectedRecipe.tip ? ` • ${selectedRecipe.tip}` : ''}
                  </div>
                )}
              </div>
              <div>
                <label style={labelStyle}>Lokasyon *</label>
                <SearchableSelect options={lokasyonOptions} value={lokasyonId ? parseInt(lokasyonId) : ''} onChange={v => setLokasyonId(v)} placeholder="Lokasyon seçiniz…" />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: spacing.base, marginBottom: spacing.base }}>
              <div>
                <label style={labelStyle}>Miktar *</label>
                <input
                  type="number"
                  step={selectedOzelKural?.tamSayi ? "1" : "0.1"}
                  min={selectedOzelKural?.tamSayi ? "1" : "0"}
                  value={uretimMiktari}
                  onChange={e => setUretimMiktari(e.target.value)}
                  placeholder={selectedOzelKural?.tamSayi ? "11" : "5.0"}
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>Birim</label>
                <input
                  value={birim}
                  onChange={e => setBirim(e.target.value)}
                  readOnly={Boolean(selectedOzelKural)}
                  style={{
                    ...inputStyle,
                    ...(selectedOzelKural ? { background: '#f8fafc', cursor: 'default' } : {}),
                  }}
                />
              </div>
              <div>
                <label style={labelStyle}>Tarih</label>
                <input type="date" value={uretimTarihi} onChange={e => setUretimTarihi(e.target.value)} style={inputStyle} />
              </div>
            </div>

            <div style={{ marginBottom: spacing.lg }}>
              <label style={labelStyle}>Notlar</label>
              <textarea value={notlar} onChange={e => setNotlar(e.target.value)} placeholder="Üretim ile ilgili notlar…" rows={3}
                style={{ ...inputStyle, resize: 'vertical' }} />
            </div>

            <button onClick={handleCreate} disabled={submitting}
              style={btn(submitting ? colors.textTertiary : colors.success)}>
              {submitting ? 'Oluşturuluyor…' : 'Üretim Kaydı Oluştur'}
            </button>
            </EnterNavForm>
          </div>
        )}

        {/* ════ DETAY ════ */}
        {detayGoster && (
          <div style={{ ...cardStyle, padding: spacing.xl }}>
            {detayLoading && !selectedRun ? (
              <div style={{ padding: spacing['2xl'], textAlign: 'center', color: colors.textSecondary }}>Detay yükleniyor…</div>
            ) : !selectedRun ? (
              <div style={{ padding: spacing['2xl'], textAlign: 'center', color: colors.textTertiary }}>
                Üretim kaydı bulunamadı.
                <div style={{ marginTop: spacing.base }}>
                  <button onClick={() => setRunParam('')} style={ghostBtn}>← Listeye Dön</button>
                </div>
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: spacing.base, marginBottom: spacing.lg, flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm }}>
                      <h3 style={{ margin: 0, fontSize: '18px', color: colors.textPrimary }}>{selectedRun.uretim_no}</h3>
                      <StatusBadge status={selectedRun.durum} />
                    </div>
                    <div style={{ fontSize: '13px', color: colors.textSecondary, marginTop: spacing.xs }}>
                      {selectedRun.recipe_ad} • {miktarFmt(selectedRun.uretim_miktari)} {selectedRun.birim} • {selectedRun.lokasyon_ad} • {selectedRun.uretim_tarihi}
                    </div>
                    {selectedRun.cikti_fire_orani > 0 && (
                      <div style={{ fontSize: '13px', color: colors.warning, marginTop: spacing.xs }}>
                        Çıktı firesi %{selectedRun.cikti_fire_orani} → stoğa net{' '}
                        <b>{miktarFmt(selectedRun.net_uretim_miktari ?? (selectedRun.uretim_miktari * (1 - selectedRun.cikti_fire_orani / 100)))} {selectedRun.birim}</b>
                        {' '}(brüt {miktarFmt(selectedRun.uretim_miktari)} {selectedRun.birim})
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: spacing.sm, alignItems: 'center' }}>
                    {selectedRun.durum === 'TASLAK' && (
                      <>
                        <button onClick={() => askTamamla(selectedRun.id)} style={btn(colors.success)}>Üretimi Tamamla</button>
                        <button onClick={() => askDelete(selectedRun.id)} style={btn(colors.danger)}>Sil</button>
                      </>
                    )}
                    <button onClick={() => setRunParam('')} style={ghostBtn}>← Geri</button>
                  </div>
                </div>

                {/* Hazırlık banner'ı (yalnız taslak + içerik görünürken) */}
                {selectedRun.durum === 'TASLAK' && !selectedRun.kilitli && cikisLines.length > 0 && (
                  yetersizLines.length > 0 ? (
                    <div style={{ background: colors.dangerLight, border: `1px solid ${colors.danger}33`, borderRadius: radius.md, padding: '12px 16px', marginBottom: spacing.base, fontSize: '13px', color: '#991b1b' }}>
                      <b>⚠️ Bazı hammaddeler yetersiz.</b>
                      <div style={{ marginTop: spacing.xs }}>
                        {yetersizLines.map((l, i) => (
                          <span key={i}>
                            {l.malzeme_ad || `Malzeme #${l.malzeme_id}`} (mevcut {miktarFmt(l.mevcut_stok)} / gerekli {miktarFmt(l.teorik_miktar)})
                            {i < yetersizLines.length - 1 ? ', ' : ''}
                          </span>
                        ))}
                      </div>
                      <div style={{ marginTop: spacing.xs, opacity: 0.85 }}>İlgili lokasyona stok girişi yapıp tekrar deneyin — ya da “Üretimi Tamamla” ile yine de kaydedin (eksik kalemler negatif stoğa düşer, negatif-stok panelinde izlenir).</div>
                    </div>
                  ) : uretimeHazir && (
                    <div style={{ background: colors.successLight, border: `1px solid ${colors.success}33`, borderRadius: radius.md, padding: '12px 16px', marginBottom: spacing.base, fontSize: '13px', color: '#166534' }}>
                      ✅ Tüm hammaddeler yeterli — üretim tamamlanmaya hazır.
                    </div>
                  )
                )}

                {selectedRun.notlar && (
                  <div style={{ background: colors.backgroundAlt, padding: '12px 16px', borderRadius: radius.sm, marginBottom: spacing.base, fontSize: '14px', color: colors.textSecondary, borderLeft: `3px solid ${colors.border}` }}>
                    {selectedRun.notlar}
                  </div>
                )}

                <h4 style={{ color: colors.textPrimary, fontSize: '14px', margin: `${spacing.sm} 0` }}>Hammadde Detayı</h4>
                {selectedRun.kilitli ? (
                  <div style={{ padding: spacing.lg, background: colors.warningLight, border: `1px solid ${colors.warning}44`, borderRadius: radius.sm, color: '#92400e', textAlign: 'center', fontSize: '14px' }}>
                    🔒 Gizli içerik — bu reçetenin hammadde detayını görme yetkiniz yok.
                  </div>
                ) : (
                  <div style={{ overflowX: 'auto', border: `1px solid ${colors.border}`, borderRadius: radius.md }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: colors.backgroundAlt, color: colors.textSecondary, fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                          <th style={{ padding: '10px 12px', textAlign: 'left' }}>Malzeme</th>
                          <th style={{ padding: '10px 12px', textAlign: 'center' }}>Tip</th>
                          <th style={{ padding: '10px 12px', textAlign: 'right' }}>Miktar</th>
                          <th style={{ padding: '10px 12px', textAlign: 'right' }}>Mevcut Stok</th>
                          <th style={{ padding: '10px 12px', textAlign: 'center' }}>Durum</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(selectedRun.lines || []).map((line, idx) => {
                          const giris = line.hareket_tipi === 'GIRIS';
                          return (
                            <tr key={idx} style={{ borderTop: `1px solid ${colors.borderLight}`, background: giris ? colors.successLight + '66' : 'transparent' }}>
                              <td style={{ padding: '10px 12px', fontWeight: 600, color: colors.textPrimary }}>{line.malzeme_ad || `Malzeme #${line.malzeme_id}`}</td>
                              <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                                <span style={{
                                  padding: '3px 8px', borderRadius: radius.full, fontSize: '11px', fontWeight: 600,
                                  background: giris ? colors.successLight : colors.warningLight,
                                  color: giris ? '#166534' : '#92400e',
                                }}>{giris ? 'ÜRÜN' : 'HAMMADDE'}</span>
                              </td>
                              <td style={{ padding: '10px 12px', textAlign: 'right', color: colors.textPrimary }}>{miktarFmt(line.teorik_miktar)} {line.birim}</td>
                              <td style={{ padding: '10px 12px', textAlign: 'right', color: colors.textSecondary }}>
                                {giris ? '—' : `${miktarFmt(line.mevcut_stok)} ${line.malzeme_birim || line.birim}`}
                              </td>
                              <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                                {!giris && (
                                  <span style={{
                                    padding: '3px 10px', borderRadius: radius.full, fontSize: '11px', fontWeight: 600,
                                    background: line.yeterli ? colors.successLight : colors.dangerLight,
                                    color: line.yeterli ? '#166534' : '#991b1b',
                                  }}>{line.yeterli ? 'Yeterli' : 'Yetersiz'}</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      <ConfirmModal
        open={Boolean(confirm)}
        title={confirm?.title}
        message={confirm?.message}
        confirmLabel={confirm?.confirmLabel}
        danger={confirm?.danger}
        busy={actionBusy}
        onConfirm={() => confirm?.onConfirm?.()}
        onCancel={() => { if (!actionBusy) setConfirm(null); }}
      />
    </div>
  );
};

export default UretimKaydiPage;
